/**
 * `/v1/products` — the list query and the create body.
 *
 * **Parameter names are transcribed from `Technical Requirements` §5, not from the
 * re-plan**, which is that document's own instruction: the plan quotes mechanisms, so the
 * plan is not a source. §5's products line reads
 *
 * >  `/products    M; ?q= ?status= ?workspace=; bulk PATCH /products:batch (≤500)`
 *
 * and §5's conventions line adds cursor pagination — `?cursor=`, `?limit=`, opaque keyset
 * cursors, an `X-Next-Cursor` header.
 *
 * ## One name §5 does not give, and it is flagged rather than invented
 *
 * The Block D re-plan requires **server-side single-column sort** on this list, and §5
 * names no parameter for it. `sort` and `order` below are therefore this file's
 * invention, and the honest thing is to say so where the next reader meets them rather
 * than let a plan requirement quietly become a spec fact. They are additions §5 is owed —
 * recorded in the Drift Log, to be written into §5 by the commit that ships them, per the
 * standing rule that a spec sentence lands when it becomes true.
 *
 * ## The cursor is opaque, and that is a contract rather than a convenience
 *
 * §5 says *opaque keyset cursors*. Opaque means a client may not construct one, which is
 * what lets the encoding change without breaking an integrator — and keyset rather than
 * offset because an offset page silently skips or repeats rows under a concurrent insert,
 * which on a catalogue being imported into is the normal case rather than the exotic one.
 * The schema therefore accepts an arbitrary string and the op decides what it means;
 * validating its *shape* here would make the encoding part of the public contract by
 * accident.
 */
import { z } from "zod";
import { hasValidCheckDigit, withCheckDigit } from "@packagepage/gs1";
import { PRODUCT_STATUSES } from "../catalog.js";
import { GTIN14 } from "./patterns.js";
/**
 * Rows per page when the caller does not say.
 *
 * `components.md` specifies cursor fetches of 100 for the dense table, so that is the
 * default rather than a round number chosen here.
 */
export const DEFAULT_PRODUCT_PAGE_SIZE = 100;
/**
 * The ceiling, and it is a real bound rather than a formality.
 *
 * The dense table's own worked example is a 912-SKU brand; a caller asking for all of
 * them in one page is asking the API to hold the whole catalogue in memory and the
 * browser to render it. 500 matches §5's own `bulk PATCH … (≤500)` bound, so the two
 * limits on this resource are one number rather than two.
 */
export const MAX_PRODUCT_PAGE_SIZE = 500;
/** Columns the list may be ordered by. A closed set — see the header on `sort`. */
export const PRODUCT_SORT_COLUMNS = ["createdAt", "name", "gtin14", "status"];
/**
 * `GET /v1/products` — every filter, in one definition.
 *
 * Query strings arrive as strings, so `limit` is coerced and everything else is taken as
 * written. **Nothing here is normalised**: `q` is not trimmed or case-folded by the
 * schema, because whether a search is case-sensitive is a decision about the *query*, and
 * making it here would hide it from the op that runs it.
 */
export const productListQuerySchema = z
    .object({
    /**
     * Free text across name, SKU and GTIN.
     *
     * **GTIN matches by substring**, which is a product decision rather than an
     * implementation detail: nobody types a fourteen-digit number from the start, and
     * `components.md` requires the matched span to be marked in the rendered row.
     */
    q: z.string().min(1, "A search needs at least one character.").optional(),
    /** One catalogue status. The vocabulary the `products_status_check` constraint holds. */
    status: z.enum(PRODUCT_STATUSES).optional(),
    /**
     * A workspace id, per §5's `?workspace=`.
     *
     * Accepted even though workspace *management* is deferred out of Phase 2 (R9, ruled
     * 2026-08-07): the column exists, every organization gets a default workspace at
     * bootstrap, and a filter that reads a real column is not the same surface as the
     * routes that manage it.
     */
    workspace: z.uuid("workspace must be a workspace id.").optional(),
    /** Opaque. See the header — the schema deliberately does not know its shape. */
    cursor: z.string().min(1).optional(),
    limit: z.coerce
        .number("limit must be a whole number of rows.")
        .int("limit must be a whole number of rows.")
        .min(1, "limit must be at least 1.")
        .max(MAX_PRODUCT_PAGE_SIZE, `limit is at most ${MAX_PRODUCT_PAGE_SIZE}.`)
        .default(DEFAULT_PRODUCT_PAGE_SIZE),
    sort: z.enum(PRODUCT_SORT_COLUMNS).default("createdAt"),
    order: z.enum(["asc", "desc"]).default("desc"),
})
    .describe("GET /v1/products — filters, keyset cursor and single-column sort.");
/**
 * `POST /v1/products`.
 *
 * **The wire this replaces may not move.** Two of its refusals carry codes an integrator
 * branches on and the suite pins: a malformed GTIN is `INVALID_GTIN`, not
 * `VALIDATION_FAILED`. `params.problemCode` is how a rule says which code it refuses
 * with — read defensively against the registry by `refuseFromSchema`, so a typo falls
 * back rather than inventing a code.
 *
 * `status` is absent and cannot be supplied. A product is created `draft`, always:
 * publishing is a separate act with its own permission and its own side effects, and a
 * create that could land `published` would skip both.
 *
 * ## Not `.strict()`, and the difference from `orgs` is the whole rule
 *
 * This shipped as `.strictObject()` for about an hour and the suite caught it: a test
 * pins that `POST` carrying `status: "published"` is **ignored** and answers 201, and
 * strict made it a 400. The shipped route reads the keys it wants and drops the rest, so
 * strictness moved the wire.
 *
 * **Strictness is a per-resource decision about what an unknown field means**, and there
 * are exactly two answers in this API:
 *
 *   - **`orgs` is strict**, because an unknown `id` is a *forged security boundary* and
 *     the route is new — nothing was ignoring it before, and refusing by name is the
 *     louder half of a two-mechanism guard.
 *   - **Everything migrated is not**, because the routes they replace ignore extras, and a
 *     migration that starts refusing them is a breaking change to a paid surface wearing
 *     the clothes of a tightening.
 *
 * The rule for a *new* resource: strict unless a caller has some legitimate reason to
 * send a field this version does not know about. The rule for a *migrated* one: match
 * what shipped, and change it later with a spec amendment if it should change.
 */
export const createProductSchema = z
    .object({
    /**
     * Fourteen digits, **and the last one has to verify over the first thirteen** (R12,
     * ruled 2026-08-08).
     *
     * ## Two refusals, in this order, and the order is the rule
     *
     * Shape first: a value that is not fourteen digits answers `INVALID_GTIN` with the
     * message the route this replaced already sent, byte for byte. Only a value that
     * passed that shape test reaches the arithmetic, so the two codes cannot swallow each
     * other and no existing wire moves.
     *
     * ## Why this is here and not in the route, the form, or the database
     *
     * **Here, because here is the only door.** Measured across the tree rather than
     * assumed: every other resource that touches a GTIN takes a `productId` and reads the
     * code off the product row — variants, batches, media, links, and the recall path,
     * which reads `gtin14` from the row it targets. No update path accepts a GTIN at all.
     * So one rule on this schema covers the authenticated API, and both transports and the
     * SPA's form inherit it — which is what R7 buys and why a per-route re-spelling would
     * be the two-types outcome that ruling forbids.
     *
     * **Not in the form alone**: a client stricter than its server teaches a user the
     * value is checked when the API would accept it. **Not in the database**: the
     * arithmetic is not expressible as a regex, so a CHECK would be a new constraint class
     * and a `23514` cannot carry the sentence below. **Not at publish**: one mechanism, at
     * the point of entry, rather than a second guard whose disagreement with the first is
     * the thing nobody tests.
     *
     * ## The recall path is deliberately NOT covered, and that is a ruling rather than an
     * omission
     *
     * If a bad-digit GTIN ever reaches a product row by some path that goes around this
     * API, a recall on that product still publishes. The recall path performs no GTIN
     * validation, ever — invariant 3, and this sentence exists so a later refactor does
     * not helpfully add one.
     *
     * ## The corrected number is carried and never applied
     *
     * A check digit is a function of the other thirteen, so the right answer is derivable
     * and it rides on the refusal — **and it is worth being exact about where, because the
     * first version of this paragraph was not.** `params.corrected` is on the zod issue,
     * which is what the **SPA** reads: the form validates with this schema directly, so it
     * has the structured value and `screens.md` §1's one-click fix has something to carry.
     * A **machine client over REST or tRPC does not**: `refuseFromSchema` forwards the code
     * and the message and nothing else, so the corrected number reaches the wire only as
     * prose inside `detail`. Putting it on the wire as a structured member would be a new
     * problem-body extension — legal under the problem-body convention, which already
     * allows one (§8.1's `remedy`), but a wire addition §5 would have to name, and not
     * something to acquire as a side effect of a docblock.
     *
     * `screens.md` §1's one-click fix is the *user* accepting it.
     * The platform never rewrites a customer's GTIN (§9's import ruling, same reasoning at
     * a different door), and the copy deliberately **does not claim to know which digit was
     * mistyped**: any single-digit error or transposition changes the sum, so the
     * arithmetic proves the code wrong and says nothing about where or why.
     */
    gtin14: z.string("A GTIN-14 is exactly 14 digits.").superRefine((value, ctx) => {
        if (!GTIN14.test(value)) {
            ctx.addIssue({
                code: "custom",
                message: "A GTIN-14 is exactly 14 digits.",
                // The code this rule refuses with, preserved from the route it replaces.
                params: { problemCode: "INVALID_GTIN" },
            });
            return;
        }
        if (hasValidCheckDigit(value))
            return;
        const corrected = withCheckDigit(value.slice(0, 13));
        ctx.addIssue({
            code: "custom",
            message: `The last digit of a GTIN is a check digit computed from the other thirteen. ` +
                `For ${value.slice(0, 13)} it is ${corrected.slice(13)}, which makes the GTIN ` +
                `${corrected}. Check the number against your source before accepting that.`,
            params: { problemCode: "INVALID_CHECK_DIGIT", corrected },
        });
    }),
    /**
     * Which workspace the product belongs to.
     *
     * Required, because `products.workspace_id` is `NOT NULL` with a foreign key and the
     * API has never defaulted it. Every organization has exactly one workspace in Phase 2
     * — written by the bootstrap — and the dashboard reads it rather than assuming its
     * slug, because "there is only one" is a fact about today's product and not about the
     * schema.
     */
    workspaceId: z.uuid("workspaceId and brandName are required."),
    brandName: z
        .string("workspaceId and brandName are required.")
        .trim()
        .min(1, "workspaceId and brandName are required."),
    sku: z.string().trim().min(1).optional(),
    /**
     * The localized name map — language tag to string.
     *
     * A record rather than a flat string, and **the schema does not pick a language**:
     * per-field language switching is the product detail screen's job, at the field and
     * never page-wide. A schema that flattened it would make that decision in the wrong
     * place and make it unrecoverable.
     */
    name: z.record(z.string(), z.string()).optional(),
})
    .describe("POST /v1/products — a product is created draft, always.");
//# sourceMappingURL=products.js.map